import React from "react";
import { Check, X, Clock, Minus } from "lucide-react";

function fmtDate(v) {
  if (!v) return null;
  const dt = new Date(v);
  if (isNaN(dt)) return null;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

function histAt(hist, action) {
  const e = (hist || []).find((h) => h.action === action);
  return e ? e.at : null;
}

// अर्जदार अर्ज मागोवा — ६ टप्पे
export function buildStages(app) {
  if (!app) return [];
  const hist = app.history || [];
  const submittedAt = app.submittedAt || histAt(hist, "submitted");
  const prelimDone = !!app.prelimDone;
  const prelimAt = app.prelimVerification?.at || histAt(hist, "prelim") || histAt(hist, "forward");

  // टप्पा ३: प्राथमिक पडताळणीमधील अंतिम निर्णय (पात्र/अपात्र)
  const elig = app.rdcDecision
    || (prelimDone && app.prelimDecision === "पात्र" && !app.forwardedToSDO && !app.forwardedToRDC ? "पात्र" : null);
  const eligDone = !!elig;
  const eligAt = app.eligibilityDecidedAt || histAt(hist, "rdc_review")
    || (elig === "पात्र" && app.prelimDecision === "पात्र" ? prelimAt : null);
  const ineligible = elig === "अपात्र";

  // टप्पा ४/५ (भविष्यातील कार्यप्रवाह)
  const visitDone = !!app.visitDone;
  const interviewDone = !!app.interviewDone;

  // टप्पा ६: अर्जावरील अंतिम निर्णय (मंजूर/नामंजूर)
  const approved = app.status === "APPROVED" || app.status === "ISSUED";
  const finalResult = approved ? "मंजूर" : (app.status === "REJECTED" || ineligible) ? "नामंजूर" : null;
  const finalAt = app.finalDecidedAt || (approved ? app.updatedAt : ineligible ? eligAt : null);

  const ended = ineligible || app.status === "REJECTED"; // अपात्र = प्रक्रिया समाप्त

  const stages = [
    { n: 1, label: "अर्ज सादर केला", done: !!submittedAt, date: submittedAt },
    { n: 2, label: "तहसीलदार यांचेकडून प्राथमिक पडताळणी", done: prelimDone, date: prelimAt },
    { n: 3, label: "प्राथमिक पडताळणीमधील अंतिम निर्णय", done: eligDone, date: eligAt, result: elig, bad: ineligible },
    { n: 4, label: "पायाभूत सुविधा तपासणी", done: visitDone, date: app.visitAt, skipped: ended },
    { n: 5, label: "मुलाखत", done: interviewDone, date: app.interviewAt, skipped: ended },
    { n: 6, label: "आपले सरकार सेवा केंद्र अर्जावरील अंतिम निर्णय", done: !!finalResult, date: finalAt, result: finalResult, bad: finalResult === "नामंजूर" },
  ];

  // "सद्यस्थिती" (live) — पहिला अपूर्ण, न वगळलेला टप्पा
  let currentSet = false;
  for (const s of stages) {
    if (!s.done && !s.skipped && !currentSet) { s.current = true; currentSet = true; }
  }
  return stages;
}

export default function ApplicationTracker({ application }) {
  const stages = buildStages(application);
  const current = stages.find((s) => s.current);
  const liveLabel = current
    ? current.label
    : (application?.status === "REJECTED" || stages.some((s) => s.bad && s.done))
      ? "प्रक्रिया समाप्त — नामंजूर / अपात्र"
      : "सर्व टप्पे पूर्ण";

  return (
    <div className="rounded-2xl border border-[#e6dcc4] bg-white p-6" data-testid="application-tracker">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[17px] font-bold text-[#1b5e3f]">अर्जाचा मागोवा (Application Tracking)</h3>
        <span className="rounded-full bg-[#faf3e0] px-3 py-1.5 text-[12.5px] font-semibold text-[#c2570a]" data-testid="tracker-live-status">
          सद्यस्थिती: {liveLabel}
        </span>
      </div>

      <ol className="relative">
        {stages.map((s, i) => {
          const isLast = i === stages.length - 1;
          const color = s.bad && s.done ? "bg-red-500 border-red-500 text-white"
            : s.done ? "bg-[#1b5e3f] border-[#1b5e3f] text-white"
            : s.current ? "bg-[#E0A106] border-[#E0A106] text-white animate-pulse"
            : s.skipped ? "bg-slate-100 border-slate-300 text-slate-400"
            : "bg-white border-slate-300 text-slate-400";
          const Icon = s.bad && s.done ? X : s.done ? Check : s.skipped ? Minus : Clock;
          const lineColor = s.done && !s.bad ? "bg-[#1b5e3f]" : "bg-slate-200";
          return (
            <li key={s.n} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`tracker-stage-${s.n}`}>
              {!isLast && <span className={`absolute left-[15px] top-8 h-[calc(100%-2rem)] w-[2px] ${lineColor}`} />}
              <span className={`z-10 flex h-8 w-8 min-w-8 items-center justify-center rounded-full border-2 ${color}`}>
                <Icon className="h-4 w-4" strokeWidth={2.5} />
              </span>
              <div className="pt-0.5">
                <p className={`text-[14px] font-semibold ${s.done ? "text-slate-800" : s.current ? "text-[#c2570a]" : "text-slate-400"}`}>
                  {s.n}. {s.label}
                  {s.result && (
                    <span className={`ml-2 rounded-full px-2 py-0.5 text-[11.5px] font-bold ${s.bad ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                      {s.result}
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-[12.5px] font-latin text-slate-500">
                  {s.done ? (fmtDate(s.date) || "पूर्ण झाले")
                    : s.current ? "प्रगतीपथावर..."
                    : s.skipped ? "लागू नाही"
                    : "प्रलंबीत"}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
